import { type CanvasBlock } from "./types";

const slugify = (text: string): string =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40) || "section";

type SectionBlock = Extract<CanvasBlock, { type: "section" }>;

const stripTrailingDividers = (children: CanvasBlock[]): CanvasBlock[] => {
  let end = children.length;
  while (end > 0 && children[end - 1].type === "divider") end -= 1;
  return end === children.length ? children : children.slice(0, end);
};

/**
 * Wraps top-level content between H2 headings into implicit `section` blocks
 * so the canvas renders as a collapsible table-of-contents instead of a flat
 * scroll. Behavior:
 *   - H1 stays at top level (closes any open section, then renders as-is).
 *   - H2 starts a new implicit section. The H2 itself becomes the section
 *     title; it is NOT also rendered as a child (would duplicate the heading).
 *   - H3 and everything else attaches to the current section if one is open,
 *     otherwise renders at top level (prelude before the first H2).
 *   - If the input already contains explicit `section` blocks, returns the
 *     input unchanged so model-emitted sections keep working.
 *   - Trailing `divider` blocks at the end of each section are stripped to
 *     avoid the LLM's "divider before next H2" pattern leaving a stray rule
 *     at the bottom of every collapsed section.
 */
export function groupBlocksBySections(blocks: CanvasBlock[]): CanvasBlock[] {
  if (blocks.some((b) => b.type === "section")) return blocks;

  const result: CanvasBlock[] = [];
  let current: SectionBlock | null = null;

  const closeCurrent = () => {
    if (!current) return;
    current.children = stripTrailingDividers(current.children);
    result.push(current);
    current = null;
  };

  blocks.forEach((block, i) => {
    if (block.type === "heading" && block.level === 1) {
      closeCurrent();
      result.push(block);
      return;
    }
    if (block.type === "heading" && block.level === 2) {
      closeCurrent();
      current = {
        type: "section",
        id: `auto-section-${i}-${slugify(block.text)}`,
        title: block.text,
        children: [],
      };
      return;
    }
    if (current) {
      current.children.push(block);
    } else {
      result.push(block);
    }
  });

  closeCurrent();
  return result;
}
